/**
 * @file     RouteChangeTracker.tsx
 * @purpose  Fires a GA4 `page_view` on every client-side route change. The
 *           landing/entry page_view is fired by gtag `config` (send_page_view),
 *           so the first render is skipped here to avoid double-counting; this
 *           covers SPA navigations, which gtag config does not.
 */
'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { env } from '@/core/config/env';
import { ga4Only } from '@/services/analytics/gtagDelivery';
import {
  confirmInternalListingEntry,
  installInternalListingClickTracking,
  isStandaloneListingPath,
} from '@/services/auth/listingEntrySource';

/** Depth marks the registry accepts. */
const SCROLL_MARKS = [25, 50, 75, 100] as const;

function gtagPageView(): void {
  if (typeof window === 'undefined') return;
  window.gtag?.(
    'event',
    'page_view',
    ga4Only({
      page_location: window.location.href,
      page_path: window.location.pathname + window.location.search,
      page_title: document.title,
    }),
  );
}

/**
 * Headless tracker. Mount once near the analytics scripts in the root layout.
 * Uses `usePathname` only (not `useSearchParams`) so it never opts routes out
 * of static rendering.
 */
export function RouteChangeTracker() {
  const pathname = usePathname();
  const isFirstRender = useRef(true);
  const previousPathname = useRef<string | null>(null);

  useEffect(() => installInternalListingClickTracking(), []);

  useEffect(() => {
    const previous = previousPathname.current;
    previousPathname.current = pathname;
    if (previous !== null && previous !== pathname && isStandaloneListingPath(pathname)) {
      confirmInternalListingEntry(pathname);
    }

    if (!env.enableAnalytics) return;
    // First-party page.view fires on entry AND client navigations; it feeds
    // the FUB bridge re-engagement rule, which GA4 page_view cannot.
    import('@/services/analytics/relta/client')
      .then(({ reltaAnalytics }) => {
        reltaAnalytics.track('page.view', { path: pathname, title: document.title });
        // route.change distinguishes an in-app navigation from a fresh entry,
        // which page.view alone cannot. Declared in the registry since launch
        // and never emitted until now.
        if (previous !== null && previous !== pathname) {
          reltaAnalytics.track('route.change', { from: previous, to: pathname });
        }
      })
      .catch(() => { /* analytics never blocks navigation */ });
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; // entry page_view comes from gtag config (send_page_view)
    }
    gtagPageView();
  }, [pathname]);

  // Reading depth per page. The only engagement signal that separates a
  // bounce from someone who read to the end; also previously declared and
  // never emitted.
  useEffect(() => {
    if (!env.enableAnalytics) return;
    const fired = new Set<number>();
    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const pct = ((window.scrollY / scrollable) * 100) as number;
      for (const mark of SCROLL_MARKS) {
        if (pct >= mark && !fired.has(mark)) {
          fired.add(mark);
          void import('@/services/analytics/relta/client')
            .then(({ reltaAnalytics }) => reltaAnalytics.track('scroll.depth', { pct: mark }))
            .catch(() => { /* analytics never blocks scrolling */ });
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pathname]);

  return null;
}
